import React, { useMemo } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import LoadingSpinner from './LoadingSpinner';

interface PermissionProtectedRouteProps {
  children: React.ReactNode;
  requiredScreenCode?: string;
  requiredScreenCodes?: string[];
  adminOnly?: boolean;
}

const PermissionProtectedRoute: React.FC<PermissionProtectedRouteProps> = ({ 
  children, 
  requiredScreenCode,
  requiredScreenCodes,
  adminOnly = false,
}) => {
  const { user, isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  const isAdmin = useMemo(() => {
    if (!user) return false;

    if (user.isAdmin) {
      return true;
    }
    
    if (user.roles && Array.isArray(user.roles)) {
      const hasAdminRole = user.roles.some((role) => {
        const roleName = typeof role === 'string' ? role : role?.name;
        const roleLower = roleName?.toLowerCase()?.trim();
        return roleLower === 'administrator' || roleLower === 'admin';
      });
      if (hasAdminRole) {
        return true;
      }
    }
    
    if (user.role && typeof user.role === 'string') {
      const roleLower = user.role.toLowerCase().trim();
      if (roleLower === 'administrator' || roleLower === 'admin') {
        return true;
      }
    }
    
    const employeeIdStr = String(user.employeeId || '');
    if (employeeIdStr === '0001' || employeeIdStr === '1') {
      return true;
    }
    
    return false;
  }, [user]);

  const hasPermission = useMemo(() => {
    if (!user) return false;
    const userScreenAccess = user.screenAccess || user.screens || [];
    if (requiredScreenCodes?.length) {
      return requiredScreenCodes.some((code) => userScreenAccess.includes(code));
    }
    if (requiredScreenCode) {
      return userScreenAccess.includes(requiredScreenCode);
    }
    return false;
  }, [user, requiredScreenCode, requiredScreenCodes]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!isAuthenticated || !user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (adminOnly && !isAdmin) {
    return <Navigate to="/profile" replace />;
  }

  if (isAdmin || hasPermission) {
    return <>{children}</>;
  }

  return <Navigate to="/profile" replace />;
};

export default PermissionProtectedRoute;
